/* eslint-disable react/jsx-props-no-spreading */
import {
    useCallback,
    useRef,
} from 'react'
import noop from 'lodash/noop'
import omit from 'lodash/omit'

import InputSingleline from 'app/shared-components/InputSingleline'
import useOutsideClick from 'app/hooks/useOutsideClick'

import InputMultiline from '../InputMultiline'

import useStyles from './Typeahead.styles'

export type InputVariant = 'standard' | 'outlined' | 'filled'

type Props = {
    searchPhrase: string;
    name?: string;
    labelData?: {
        value?: string;
        iconPath?: string;
    };
    onChange?: (value: any, event: { target: { name: string; value: any } }) => void;
    title?: string;
    setSearchPhrase: (phrase: string) => void;
    options: Array<{
        label?: string | JSX.Element;
        [key: string]: any;
    }>;
    optionsVisible: boolean;
    labelField: string;
    valueField: string;
    openOptions: () => void;
    closeOptions: () => void;
    variant?: InputVariant;
    classNames?: {
        options?: string,
    };
    className?: string;
    isRequired?: boolean;
    multiline?: boolean;
    getLabelValue?: (option: any, labelField: string) => string;
    errors?: string[];
    validated?: boolean;
    noneOption?: boolean;
}

const defaultProps: Partial<Props> = {
    labelData: {},
    name: '',
    onChange: noop,
    options: [],
    errors: undefined,
    title: '',
    variant: 'outlined',
    classNames: {},
    className: '',
    isRequired: false,
    validated: undefined,
    multiline: false,
    getLabelValue: noop,
    noneOption: true,
}

const removeFields = (obj: Props) => {
    return omit(obj, [
        'value',
        'Comp',
        'valueField',
        'labelField',
        'loadLabel',
        'loadOptions',
        'options',
        'loadAirports',
        'className',
    ])
}

const Typeahead = (props: Readonly<Props>) => {
    const {
        onChange,
        options,
        openOptions,
        optionsVisible,
        setSearchPhrase,
        closeOptions,
        searchPhrase,
        valueField,
        variant,
        labelData,
        name,
        classNames,
        className,
        isRequired,
        multiline,
        getLabelValue,
        labelField,
        noneOption,
    } = props // destruct props here to pass them also to input

    const {
        value,
        iconPath,
    } = labelData

    const {
        classes,
        cx,
    } = useStyles()

    const wrapperRef = useRef<HTMLDivElement>()

    useOutsideClick(wrapperRef, closeOptions)

    const onOptionClick = useCallback((event) => {
        const {
            dataset: {
                value: newValue,
            },
        } = event.currentTarget || event.target

        const optionValue = options.find(({
            id,
        }) => {
            return String(id) === String(newValue)
        }) || (newValue ? {
            id: newValue,
        } : undefined)

        onChange(optionValue, {
            target: {
                name,
                value: optionValue,
            },
        })
        closeOptions()
    }, [
        closeOptions,
        name,
        onChange,
        options,
    ])

    const onEnterPress = useCallback((event) => {
        const {
            key,
        } = event

        if (optionsVisible && key === 'Enter') {
            onChange(options[0], {
                target: {
                    name,
                    value: options[0],
                },
            })
            closeOptions()
        }
    }, [
        closeOptions,
        name,
        onChange,
        options,
        optionsVisible,
    ])

    const InputComponent = multiline ? InputMultiline : InputSingleline

    return (
        <div
            data-testid={`typeahead-${name}`}
            className={cx(classes.root, className)}
            ref={wrapperRef}
        >
            <InputComponent
                {...removeFields(props)}
                required={isRequired}
                onChange={setSearchPhrase}
                classNames={classNames}
                value={optionsVisible ? searchPhrase : value}
                onKeyPress={onEnterPress}
                onFocus={openOptions}
                variant={variant}
                iconPath={iconPath}
                autoComplete="off"
                name={name.replace('Id', '')} // needed to remove 'Id',strangely lastpass flagged it as user/pass field
            />
            <div
                className={
                    cx(
                        classes.optionsWrapper,
                        optionsVisible && 'active',
                    )
                }
                data-testid="typeahead-options-wrapper"
            >
                {(options.length > 0 || searchPhrase) && (
                    <div
                        className={cx(
                            classes.options,
                            classNames.options,
                        )}
                        style={{
                            width: wrapperRef.current?.offsetWidth,
                        }}
                    >
                        {noneOption && options.length > 0 && (
                            <button
                                type="button"
                                key="option-none"
                                onClick={onOptionClick}
                                className={cx(classes.option, classes.optionNone)}
                                data-value={undefined}
                                title="None"
                            >
                                None
                            </button>
                        )}

                        {options.length === 0 && searchPhrase && (
                            <div className={cx(
                                classes.option,
                                classes.noOptions,
                                classes.optionNone,
                            )}
                            >
                                No Options
                            </div>
                        )}

                        {options.map((option) => {
                            return (
                                <button
                                    type="button"
                                    key={`option-${option[valueField]}`}
                                    onClick={onOptionClick}
                                    className={classes.option}
                                    data-value={option[valueField]}
                                >
                                    {option.label || getLabelValue(option, labelField)}
                                </button>
                            )
                        })}
                    </div>
                )}
            </div>
        </div>
    )
}

Typeahead.defaultProps = defaultProps

export default Typeahead
